/**
 * useDetailsScreen - Hook for managing user details form screen logic
 *
 * Handles the complex logic for the details screen which supports both:
 * 1. New users entering their information for the first time
 * 2. Existing users completing missing profile fields (partial profile mode)
 *
 * This hook manages:
 * - Stepper configuration based on user type
 * - Form submission with different paths for new vs existing users
 * - Payment setup (SetupIntent creation) for authenticated users without billing
 * - Form validation based on required/missing fields
 * - Back navigation based on user's entry point
 */
import { useState } from 'react';

import { CREATE_SMB_SETUP_INTENT_MUTATION } from '@/auth/store/queries';
import { authSession } from '@/auth/utils/authSession';
import { MfaSetupAtLoginError, mfaSetupAtLogin } from '@/auth/utils/cognitoMfaSetup';

import { OTP_STEPS } from '../constants';
import type {
  BillingAccountInfo,
  MissingField,
  ProfileState,
  Screen,
  SignupFormData,
  StripeState,
  UserData,
  UserDetailsFormValues,
} from '../types';
import { validateForm } from '../validation';

export interface UseDetailsScreenOptions {
  userData: UserData | null;
  profileState: ProfileState | null;
  isAuthenticatedUserMode: boolean;
  otpVerificationToken: string | null;
  signupFormData: SignupFormData | null;
  isLoading: boolean;
  error: string | null;
  missingFields: MissingField[];
  partialProfileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    company?: string;
  } | null;
  isExistingUserFromEmailLogin: boolean;
  billingAccount: BillingAccountInfo | null;
  onNavigate: (screen: Screen) => void;
  onSetStripeState: (state: StripeState) => void;
  onSetSignupFormData: (data: SignupFormData) => void;
  onSubmitNewUser: (values: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    jobTitle?: string;
    password: string;
  }) => Promise<void>;
}

export interface UseDetailsScreenResult {
  isExistingUserCompletingProfile: boolean;
  steps: readonly [string, string, string] | readonly [string, string];
  stepIndex: number;
  initialValues:
    | {
        firstName?: string;
        lastName?: string;
        email?: string;
        company?: string;
      }
    | SignupFormData
    | undefined;
  isSettingUpPayment: boolean;
  isLoading: boolean;
  error: string | null;
  /** ENG-6106: the api refused the session for MFA setup; only sign-in helps. */
  signInRequired: boolean;
  missingFields: MissingField[];
  handleSubmit: (values: Record<string, unknown>) => Promise<void>;
  validate: (values: Record<string, unknown>) => Partial<UserDetailsFormValues>;
  handleBack: () => void;
}

export const useDetailsScreen = (options: UseDetailsScreenOptions): UseDetailsScreenResult => {
  const {
    userData: _userData,
    profileState: _profileState,
    isAuthenticatedUserMode,
    otpVerificationToken: _otpVerificationToken,
    signupFormData,
    isLoading,
    error,
    missingFields,
    partialProfileData,
    isExistingUserFromEmailLogin,
    billingAccount,
    onNavigate,
    onSetStripeState,
    onSetSignupFormData,
    onSubmitNewUser,
  } = options;

  const [isSettingUpPayment, setIsSettingUpPayment] = useState(false);
  const [signInRequiredError, setSignInRequiredError] = useState<string | null>(null);

  // Determine if this is an existing user completing their profile
  const isExistingUserCompletingProfile = partialProfileData !== null && missingFields.length > 0;

  // Stepper configuration
  const steps = isExistingUserCompletingProfile ? OTP_STEPS.RETURNING_USER : OTP_STEPS.NEW_USER;
  const stepIndex = isExistingUserCompletingProfile ? 0 : 1;

  // Initial form values
  const initialValues = isExistingUserCompletingProfile
    ? partialProfileData
    : signupFormData || undefined;

  // Handle form submission
  const handleSubmit = async (values: Record<string, unknown>) => {
    if (isExistingUserCompletingProfile) {
      // Existing user completing profile - just need the missing fields
      const firstName = (values.firstName as string) || partialProfileData?.firstName || '';
      const lastName = (values.lastName as string) || partialProfileData?.lastName || '';
      const company = (values.company as string) || partialProfileData?.company || '';
      const email = partialProfileData?.email || '';

      // Save form data
      onSetSignupFormData({ firstName, lastName, email, company, password: '' });

      // Check if they need billing setup
      if (!billingAccount) {
        // Check if user is authenticated (via authenticatedViewer prop, email login, OR OTP returning user)
        const token = authSession.getToken();
        const isAuthenticated = isAuthenticatedUserMode || isExistingUserFromEmailLogin || !!token;

        // For authenticated users, create SetupIntent using bearer auth
        if (isAuthenticated) {
          setIsSettingUpPayment(true);
          try {
            if (!token) {
              throw new Error('Authentication required');
            }

            // ENG-6106: no captured token, so a Cognito session's client
            // reads its ID token per request.
            const client = authSession.createClient();
            const { data: setupData } = await client.client.mutate({
              mutation: CREATE_SMB_SETUP_INTENT_MUTATION,
              // ENG-6106: mfaSetupAtLogin answers the MFA gate, not mfaGateLink.
              context: { skipMfaGateRedirect: true },
              variables: {
                input: {
                  companyName: company || undefined,
                },
              },
            });

            if (setupData?.createSMBSetupIntent) {
              onSetStripeState({
                clientSecret: setupData.createSMBSetupIntent.clientSecret,
                customerId: setupData.createSMBSetupIntent.stripeCustomerId,
              });
            }

            // Navigate to payment (isExistingUserWithoutBilling is derived from userData)
            onNavigate('payment-new');
          } catch (err) {
            console.error('Failed to create SetupIntent:', err);
            // Show error on current screen. ENG-6106: a Cognito session the
            // api refuses for MFA setup ends, and the screen shows the
            // login-page copy with a sign-in link instead of the form.
            const translated = await mfaSetupAtLogin(err);
            if (translated instanceof MfaSetupAtLoginError) {
              setSignInRequiredError(translated.message);
              return;
            }
            throw translated;
          } finally {
            setIsSettingUpPayment(false);
          }
        } else {
          onNavigate('payment-new');
        }
      } else {
        onNavigate('payment-existing');
      }
    } else {
      // New user - use existing flow
      await onSubmitNewUser({
        firstName: values.firstName as string,
        lastName: values.lastName as string,
        email: values.email as string,
        company: values.company as string,
        jobTitle: values.jobTitle as string | undefined,
        password: values.password as string,
      });
    }
  };

  // Validation function that accounts for existing users
  const validate = (values: Record<string, unknown>) => {
    return validateForm(values, {
      isExistingUser: isExistingUserCompletingProfile,
      missingFields,
    });
  };

  // Back navigation
  const handleBack = () => {
    // For authenticated users or email login users, go back to booking
    // (they're already authenticated, no need to show login form again)
    if (isAuthenticatedUserMode || isExistingUserFromEmailLogin) {
      onNavigate('booking');
    } else if (isExistingUserCompletingProfile) {
      // OTP returning user completing profile - go back to verify
      onNavigate('verify');
    } else {
      // New OTP user - go back to verify
      onNavigate('verify');
    }
  };

  return {
    isExistingUserCompletingProfile,
    steps,
    stepIndex,
    initialValues,
    isSettingUpPayment,
    isLoading,
    error: signInRequiredError ?? error,
    signInRequired: signInRequiredError !== null,
    missingFields,
    handleSubmit,
    validate,
    handleBack,
  };
};
